import { formatDate } from '@angular/common';
import { UserRolesCanAssignRoleItem } from '@zvoove-market/api';

interface AssignableRole {
  displayName: string;
  internalName: string;
  disabled?: boolean;
  expiresAt?: Date | null;
}

/**
 * Takes a list of roles, turns it into list of roles that is better suited for zv-select.
 * And optionally either sets non-assignable roles as disabled or removes them from the result altogether.
 * @param roles
 * @param locale
 * @param assignedRoles
 * @returns
 */
export function getAssignableRoles(
  roles: UserRolesCanAssignRoleItem[],
  locale: string,
  assignedRoles: string[],
  removeUnAssignableRoles: boolean
): AssignableRole[] {
  const assignableRoles = roles.map(
    (role) =>
      ({
        internalName: role.internalName,
        displayName: getRoleDisplayName(role, locale),
        disabled: !getRoleCanBeAssigned(role, assignedRoles),
      }) as AssignableRole
  );
  return removeUnAssignableRoles ? assignableRoles.filter((role) => !role.disabled) : assignableRoles;
}

export function getRoleDisplayName(role: Partial<UserRolesCanAssignRoleItem>, locale: string) {
  const parts = [];
  if (role.expiresAt) {
    const expiresAtText = $localize`:@@general.expiresAt:Läuft ab am`;
    const formatedDate = formatDate(role.expiresAt, 'short', locale);
    parts.push(`${expiresAtText} ${formatedDate}`);
  }

  if (role.unfulfilledConditions?.missingAssigneeAlsoNeedsRoles) {
    let rolesPart = $localize`:@@roleAssignment.alsoNeeded:Weitere Rolle benötigt` + ':';
    role.unfulfilledConditions.missingAssigneeAlsoNeedsRoles.forEach((element) => {
      rolesPart += ` ${element.displayName}`;
    });
    parts.push(rolesPart);
  }

  if (parts.length > 0) {
    return `${role.displayName} (${parts.join(', ')})`;
  }
  return role.displayName;
}

/**
 * Whether a role can be assigned or not based on the "assignee also needs" condition.
 * @param role The role that (optionally) contains a "assignee also needs" condition
 * @param assignedRoles The roles that are already assigned (and are the "also needed" roles in the condition)
 * @returns Checks whether a role can be assigned or not
 */
export function getRoleCanBeAssigned(role: UserRolesCanAssignRoleItem, assignedRoles: string[]): boolean {
  const roleIsAlreadyAssigned = assignedRoles.indexOf(role.internalName) != -1;
  if (roleIsAlreadyAssigned) {
    return true;
  }

  if (
    role.unfulfilledConditions &&
    role.unfulfilledConditions.missingAssigneeAlsoNeedsRoles.filter(
      (alsoNeededRole) => assignedRoles.indexOf(alsoNeededRole.internalName) == -1
    ).length > 0
  ) {
    return false;
  }

  return true;
}
